"use client";
import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import Link from "next/link";
import { useInstantSearch, useCurrentRefinements } from "react-instantsearch";
import type { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { ProductSummary } from "@/common/productSummary/types";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import useIsMobile from "@/hooks/useIsMobile";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import { GetStartedWithResultsSection } from "@/monarch/rules/getStartedWith/types";
import { cn } from "@/lib/utils";
import { useInView } from "react-intersection-observer";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  FormatSubType,
  FormatType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import {
  GET_STARTED_ELEMENT_LOCATION,
  GET_STARTED_ELEMENT_TYPE,
  GET_STARTED_ELEMENT_HTML_ID,
  ELEMENT_NAMES,
} from "../hooks/useGetStartedWithEvents";
import ProductCard from "@/components/ProductCard";
import { convertHitToProductCardProps } from "@/components/Search/utils/convertHitToProductCardProps";
import "./styles.css";

// Carousel hooks
type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

interface ResultsProps {
  indexName: string;
  showResults: boolean;
  onEditSelections: () => void;
  resultsSectionMonarchData: GetStartedWithResultsSection;
  isCompact?: boolean;
  skillsEmpty?: boolean;
}

export const Results = ({
  indexName,
  showResults,
  onEditSelections,
  resultsSectionMonarchData,
  isCompact = false,
  skillsEmpty = false,
}: ResultsProps) => {
  const carouselMessages = useCarouselMessages();
  const isMobile = useIsMobile();
  const { scopedResults } = useInstantSearch();
  const { items: currentRefinements, refine } = useCurrentRefinements();
  const {
    resultsSubtitle,
    secondaryCtaText,
    viewMoreOptionsButton,
    editSelectionsButton2,
  } = resultsSectionMonarchData;

  // Build search URL with current refinements (moved up for use in webElement)
  const searchUrl = useMemo(() => {
    const params = new URLSearchParams();

    currentRefinements.forEach((item) => {
      item.refinements.forEach((refinement) => {
        params.append(item.attribute, String(refinement.value));
      });
    });

    const queryString = params.toString();
    return queryString ? `/search?${queryString}` : "/search";
  }, [currentRefinements]);

  // WebElement for Edit Selections button
  const editSelectionsWebElement = useMemo(
    () => ({
      elementType: GET_STARTED_ELEMENT_TYPE,
      htmlId: GET_STARTED_ELEMENT_HTML_ID,
      location: GET_STARTED_ELEMENT_LOCATION,
      position: "1",
      name: ELEMENT_NAMES.EDIT_SELECTIONS,
      text: editSelectionsButton2,
    }),
    [editSelectionsButton2],
  );

  // WebElement for View More Options button
  const viewMoreWebElement = useMemo(
    () => ({
      elementType: GET_STARTED_ELEMENT_TYPE,
      htmlId: GET_STARTED_ELEMENT_HTML_ID,
      location: GET_STARTED_ELEMENT_LOCATION,
      position: "1",
      name: ELEMENT_NAMES.VIEW_ALL_PRODUCTS,
      text: viewMoreOptionsButton,
    }),
    [viewMoreOptionsButton],
  );

  // Event tracking for Edit Selections button
  const {
    elementClicked: editSelectionsClicked,
    elementViewed: editSelectionsViewed,
  } = useElementEvents({ webElement: editSelectionsWebElement });

  // Event tracking for View More Options button
  const { elementClicked: viewMoreClicked, elementViewed: viewMoreViewed } =
    useElementEvents({ webElement: viewMoreWebElement });

  const editBtnViewedRef = useRef(false);
  const viewMoreBtnViewedRef = useRef(false);

  const { ref: editBtnInViewRef, inView: editBtnInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );
  const { ref: viewMoreBtnInViewRef, inView: viewMoreBtnInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  // Track Edit Selections button view
  useEffect(() => {
    if (editBtnInView && !editBtnViewedRef.current && !isMobile) {
      editBtnViewedRef.current = true;
      editSelectionsViewed();
    }
  }, [editBtnInView, isMobile, editSelectionsViewed]);

  // Track View More Options button view
  useEffect(() => {
    if (viewMoreBtnInView && !viewMoreBtnViewedRef.current) {
      viewMoreBtnViewedRef.current = true;
      viewMoreViewed();
    }
  }, [viewMoreBtnInView, viewMoreViewed]);

  // Reset view tracking when results are hidden/shown
  useEffect(() => {
    if (!showResults) {
      editBtnViewedRef.current = false;
      viewMoreBtnViewedRef.current = false;
    }
  }, [showResults]);

  // Check if required filters are selected
  const subjectAttribute = getFacetAttributeName("subject", { indexName });
  const skillsAttribute = getFacetAttributeName("skills.skill", { indexName });

  const subjectSelected = currentRefinements.some(
    (item) =>
      item.attribute === subjectAttribute && item.refinements.length > 0,
  );

  const skillSelected = currentRefinements.some(
    (item) => item.attribute === skillsAttribute && item.refinements.length > 0,
  );

  // Enable results if subject is selected AND (skill is selected OR skills dropdown is empty)
  const allFiltersSelected = subjectSelected && (skillSelected || skillsEmpty);

  // Carousel setup - scroll by 2 in compact mode (shows 2 cards), by 1 on mobile (shows 1 card)
  const slidesToScroll = isCompact && !isMobile ? 2 : 1;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: false,
    slidesToScroll,
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const hits = (scopedResults?.[0]?.results?.hits ?? []) as ProductSummary[];

  const clearAllFilters = () => {
    currentRefinements.forEach((item) => {
      item.refinements.forEach((refinement) => {
        refine(refinement);
      });
    });
  };

  const handleEditSelections = () => {
    editSelectionsClicked();
    clearAllFilters();
    onEditSelections();
  };

  const handleViewMoreClick = () => {
    viewMoreClicked({ outboundUrl: searchUrl });
  };

  // Only show results if user clicked "Explore courses" AND both filters are selected
  if (!showResults || !allFiltersSelected || hits.length === 0) {
    return null;
  }

  const renderCard = (card: ProductSummary, index: number) => {
    const cardProps = convertHitToProductCardProps(card, indexName);
    // Ensure href is absolute to avoid relative URL issues on /learn pages
    const href = cardProps.href.startsWith("/")
      ? cardProps.href
      : `/${cardProps.href}`;

    return (
      <ProductCard
        {...cardProps}
        href={href}
        location="get-started-with-results"
        formatSubtype={FormatSubType.Grid}
        formatType={FormatType.ProductCard}
        index={index}
      />
    );
  };

  // Wrapper to override card max-width in carousel (styles defined in styles.css)
  const CarouselCardWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="carousel-card-wrapper w-full">{children}</div>
  );

  return (
    <div
      className={cn("not-prose", isCompact ? "mx-auto" : "mx-break-out")}
      style={{
        background: "linear-gradient(to bottom, white, hsl(var(--putty-400)))",
      }}
    >
      <div className="pb-16 pt-8 max-w-[1240px] mx-auto px-4">
        <h3 className="text-center text-xl md:text-2xl text-gray-800 mb-8">
          {resultsSubtitle}
        </h3>

        {/* Carousel: Mobile (1 card) or Compact (2 cards) */}
        {isMobile || isCompact ? (
          <>
            <div className={cn("embla get-started-with", isMobile && "mobile")}>
              <div className="embla__viewport" ref={emblaRef}>
                <div className="embla__container">
                  {hits.map((card, index) => (
                    <div key={card.productUuid} className="embla__slide">
                      <CarouselCardWrapper>
                        {renderCard(card, index)}
                      </CarouselCardWrapper>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Carousel Controls */}
            <div className="w-full mt-8 flex flex-row justify-center">
              <div className="flex flex-row justify-center w-fit items-center gap-2 bg-white rounded-full">
                {/* Previous Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onPrevButtonClick}
                  disabled={prevBtnDisabled}
                  aria-label={carouselMessages.previousSlide}
                  className="hover:bg-transparent"
                >
                  <ChevronLeft />
                </Button>

                {/* Dots */}
                <div className="flex gap-4">
                  {scrollSnaps.map((_, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      size="icon"
                      onClick={() => onDotButtonClick(index)}
                      aria-label={carouselMessages.goToSlide(index + 1)}
                      className={`h-3 w-3 rounded-full p-0 ${
                        index === selectedIndex
                          ? "bg-brand-500"
                          : "bg-primary-500"
                      }`}
                    >
                      <Circle
                        svgProps={{
                          width: "8",
                          height: "8",
                          className: `${index === selectedIndex ? "text-brand-500" : "text-primary-500"}`,
                        }}
                      />
                    </Button>
                  ))}
                </div>

                {/* Next Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onNextButtonClick}
                  disabled={nextBtnDisabled}
                  aria-label={carouselMessages.nextSlide}
                  className="hover:bg-transparent"
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Desktop Full Width: Grid */
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {hits.map((card, index) => (
              <div key={card.productUuid} className="inline">
                {renderCard(card, index)}
              </div>
            ))}
          </div>
        )}

        {/* Bottom section with text and buttons */}
        <div className="mt-12 flex flex-col items-center">
          <p className="text-primary-500 font-bold elm-p4 mb-6">
            {secondaryCtaText}
          </p>
          <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
            {!isMobile && (
              <button
                ref={editBtnInViewRef}
                onClick={handleEditSelections}
                className="h-[42px] rounded-full px-6 font-medium whitespace-nowrap border-2 bg-white text-brand border-brand hover:opacity-90 cursor-pointer transition-opacity flex items-center justify-center"
              >
                {editSelectionsButton2}
              </button>
            )}
            <Link
              ref={viewMoreBtnInViewRef}
              href={searchUrl}
              onClick={handleViewMoreClick}
              className="h-[42px] rounded-full px-6 font-medium whitespace-nowrap border-2 bg-brand text-white border-brand hover:opacity-90 cursor-pointer transition-opacity no-underline flex items-center justify-center"
            >
              {viewMoreOptionsButton}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
